import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Button,
  Typography,
  Chip,
  TextField,
  InputAdornment,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { Search as SearchIcon } from '@material-ui/icons'
import MUIDataTable from 'mui-datatables'
import axios from 'src/utils/axios'

import { usePaginatedFiles, useBedFiles } from '../../api/file'
import { useOrganisationDetails } from 'src/api/user/list'
import { API_BASE_URL } from '../../constants'

import { NewAnnotationDialog } from './AnnotationDialog'
import ReadOnlyAnnotationDialog from './ReadOnlyAnnotationDialog'

// Hooks
import { useFileActions } from './hooks/useFileActions'
import { useStableFilesData } from './hooks/useStableFilesData'
import { useStableUIState } from './hooks/useStableUIState'

// Components
import FileTableSnackbars from './components/FileTableSnackbars'
import TableLoadingOverlay from './components/TableLoadingOverlay'
import { createFilesTableColumns } from './components/FilesTableColumns'
import { useSnackbar } from 'notistack5'

const useStyles = makeStyles((theme) => ({
  wrapper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  tableContainer: {
    position: 'relative',
    minHeight: '200px',
    overflow: 'hidden', // Prevent overlay from showing outside bounds
    borderRadius: theme.shape.borderRadius,
    // Smooth row animations
    '& .smooth-row-enter': {
      animation: '$smoothSlideIn 0.4s ease-out',
    },
    '& .smooth-row-exit': {
      animation: '$smoothSlideOut 0.6s ease-out',
    },
    '& .smooth-row-update': {
      animation: '$smoothUpdateFlash 0.4s ease-in-out',
    },
  },
  filtersContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 0,
    padding: theme.spacing(2),
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  searchField: {
    minWidth: '600px',
  },
  filterChips: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  '@keyframes smoothSlideIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-20px) scale(0.98)',
      backgroundColor: 'rgba(76, 175, 80, 0.2)',
    },
    '50%': {
      opacity: 0.7,
      transform: 'translateY(-5px) scale(0.99)',
      backgroundColor: 'rgba(76, 175, 80, 0.15)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
      backgroundColor: 'rgba(76, 175, 80, 0.05)',
    },
  },
  '@keyframes smoothSlideOut': {
    '0%': {
      opacity: 1,
      transform: 'translateX(0) scale(1)',
      maxHeight: '100px',
      backgroundColor: 'transparent',
    },
    '25%': {
      opacity: 0.8,
      transform: 'translateX(-5px) scale(0.99)',
      maxHeight: '100px',
      backgroundColor: 'rgba(244, 67, 54, 0.05)',
    },
    '50%': {
      opacity: 0.5,
      transform: 'translateX(-15px) scale(0.97)',
      maxHeight: '80px',
      backgroundColor: 'rgba(244, 67, 54, 0.1)',
    },
    '75%': {
      opacity: 0.2,
      transform: 'translateX(-30px) scale(0.94)',
      maxHeight: '40px',
      backgroundColor: 'rgba(244, 67, 54, 0.08)',
    },
    '100%': {
      opacity: 0,
      transform: 'translateX(-50px) scale(0.9)',
      maxHeight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      marginTop: 0,
      marginBottom: 0,
      backgroundColor: 'transparent',
    },
  },
  '@keyframes smoothUpdateFlash': {
    '0%': {
      backgroundColor: 'rgba(33, 150, 243, 0.2)',
    },
    '50%': {
      backgroundColor: 'rgba(33, 150, 243, 0.1)',
    },
    '100%': {
      backgroundColor: 'transparent',
    },
  },
}))

const FilesTable = React.memo(() => {
  const classes = useStyles()

  // Pagination and filtering state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [filters, setFilters] = useState({
    type: ['All'],
  })
  const [sortOrder, setSortOrder] = useState('desc')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Available types
  const availableTypes = ['FASTQ Pair', 'VCF', 'ONT BAM', 'PacBio BAM']

  // Memoize the query parameters to prevent unnecessary re-renders
  const queryParams = useMemo(
    () => ({
      page: page,
      limit: rowsPerPage,
      type: filters.type.includes('All') ? [] : filters.type,
      sort_order: sortOrder,
      // Only include search if search term is at least 3 characters
      ...(debouncedSearchTerm.length >= 3 && { search: debouncedSearchTerm }),
    }),
    [page, rowsPerPage, filters.type, sortOrder, debouncedSearchTerm],
  )

  // API calls
  const filesApi = usePaginatedFiles(queryParams)
  const bedFilesApi = useBedFiles()
  const { data: bedFiles = [] } = bedFilesApi.query
  const { data: organisationDetails = {} } = useOrganisationDetails()

  // Fetch data using the hook with memoized params
  const { query, refresh } = filesApi
  const { data: filesData, isLoading, isError, isPreviousData, isFetching } = query

  // --- IMPORTANT: Transform the nested data into a flat list for the table ---
  const processedData = useMemo(() => {
    if (!filesData) return []
    // Backend returns data in 'results' field
    const ret = filesData.results.flat()
    return ret
  }, [filesData])

  const isLoadingState = isLoading || isFetching

  // Get total count for pagination
  const totalCount = useMemo(() => {
    if (!filesData) return 0
    // Backend returns total count in pagination.total_count
    return filesData.pagination?.total_count || 0
  }, [filesData])

  // Handle page change
  const handleChangePage = useCallback((currentPage) => {
    setPage(currentPage)
  }, [])

  // Handle rows per page change
  const handleChangeRowsPerPage = useCallback((numberOfRows) => {
    setRowsPerPage(numberOfRows)
    setPage(0) // Reset to first page when rows per page changes
  }, [])

  // Handle sort change
  const handleSortChange = useCallback((changedColumn, direction) => {
    console.log('Sort changed:', changedColumn, direction)
    // Only allow sorting on date field
    if (changedColumn === 'created_at') {
      setSortOrder(direction)
      setPage(0) // Reset to first page when sorting changes
    }
  }, [])

  // Stable UI state management
  const {
    isAnnotationModalOpen,
    selectedFile,
    aiDialogOpen,
    selectedAI,
    vcfIdState,
    errorModalOpen,
    neededBalance,
    setErrorModalOpen,
    setNeededBalance,
    handleAnnotationModelOpen,
    handleAnnotationModelClose,
    handleOpenAIDialog,
    handleCloseAIDialog,
    getSafeSelectedFile,
  } = useStableUIState()

  // File actions hook with memoized callbacks
  const {
    downloadingFastqId,
    downloadingReportId,
    downloadingVcfId,
    annotationStatus,
    setAnnotationStatus,
    handleDownloadReport,
    handleDownloadRawVcf,
    handleFileAnnotation,
    handleFileDelete,
    setFinishedInfo,
    setFileNotes,
    handleDownloadFastq,
    downloadingBamId,
    handleDownloadOntBam,
    handleDownloadPacBioBam,
  } = useFileActions(filesApi, {})

  // Snackbar states
  const [downloadFastqSnackbarOpen, setDownloadFastqSnackbarOpen] = useState(false)
  const [downloadFastqFailedSnackbarOpen, setDownloadFastqFailedSnackbarOpen] = useState(false)
  const [downloadBamFailedSnackbarOpen, setDownloadBamFailedSnackbarOpen] = useState(false)
  const [paymentSnackbarOpen, setPaymentSnackbarOpen] = useState(false)
  const [deductedAmount, setDeductedAmount] = useState(-1)
  const [newBalance, setNewBalance] = useState(-1)
  const [refundSnackbarOpen, setRefundSnackbarOpen] = useState(false)
  const [refundedFile, setRefundedFile] = useState(null)
  const [refundAmount, setRefundAmount] = useState('')

  // Effects - check for refunds when data is available
  useEffect(() => {
    if (processedData && processedData.length > 0) {
      const refundedFile = processedData.find((file) => file.is_refunded && !file.user_notified)
      if (refundedFile) {
        setRefundedFile(refundedFile)
        setRefundSnackbarOpen(true)
        // Check file type
        if (refundedFile?.fastq_pair_id) {
          setRefundAmount(organisationDetails.fastq_price)
        } else {
          setRefundAmount(organisationDetails.vcf_price)
        }
      }
    }
  }, [processedData, organisationDetails])

  // Event handlers with memoization
  const handleRefundSnackbarClose = useCallback(
    async (event, reason) => {
      if (reason === 'clickaway') {
        return
      }
      setRefundSnackbarOpen(false)
      if (refundedFile) {
        const { id, type } = refundedFile?.fastq_pair_id
          ? { id: refundedFile.fastq_pair_id, type: 'FASTQ' }
          : { id: refundedFile.vcf_id, type: 'VCF' }

        try {
          await axios.put(`${API_BASE_URL}/refundack/`, { fileId: id, type: type })
          filesApi.refresh()
        } catch (error) {
          console.error('Error acknowledging refund:', error)
        }
      }
    },
    [refundedFile, filesApi],
  )

  // Memoized event handlers
  const handleDownloadFastqFailed = useCallback(() => {
    setDownloadFastqFailedSnackbarOpen(true)
  }, [])

  const handleDownloadBamFailed = useCallback(() => {
    setDownloadBamFailedSnackbarOpen(true)
  }, [])

  const handleDownloadFastqSnackbarClose = useCallback(() => setDownloadFastqSnackbarOpen(false), [])
  const handleDownloadFastqFailedSnackbarClose = useCallback(() => setDownloadFastqFailedSnackbarOpen(false), [])
  const handleDownloadBamFailedSnackbarClose = useCallback(() => setDownloadBamFailedSnackbarOpen(false), [])
  const handlePaymentSnackbarClose = useCallback(() => setPaymentSnackbarOpen(false), [])

  const { enqueueSnackbar } = useSnackbar()
  const handleFileAnnotationWrapper = useCallback(
    async (annotation) => {
      const currentFile = getSafeSelectedFile()
      const result = await handleFileAnnotation(
        currentFile,
        annotation,
        setErrorModalOpen,
        setNeededBalance,
        setPaymentSnackbarOpen,
        setNewBalance,
        setDeductedAmount,
      )
      // A clean refusal (nothing created or charged): the server's reason, dialog kept open.
      if (result?.refused) {
        enqueueSnackbar(result.error, { variant: 'warning' })
        return result
      }
      if (result) {
        handleAnnotationModelClose()
      }
      return result
    },
    [
      getSafeSelectedFile,
      handleFileAnnotation,
      setErrorModalOpen,
      setNeededBalance,
      setPaymentSnackbarOpen,
      setNewBalance,
      setDeductedAmount,
      handleAnnotationModelClose,
      enqueueSnackbar,
    ],
  )

  const handleDownloadFastqWrapper = useCallback(
    (fastqPairId, sampleName) => {
      handleDownloadFastq(fastqPairId, sampleName, handleDownloadFastqFailed, () => setDownloadFastqSnackbarOpen(true))
    },
    [handleDownloadFastq, handleDownloadFastqFailed],
  )

  const handleDownloadOntBamWrapper = useCallback(
    (bamId, sampleName) => {
      handleDownloadOntBam(bamId, sampleName, handleDownloadBamFailed)
    },
    [handleDownloadOntBam, handleDownloadBamFailed],
  )

  const handleDownloadPacBioBamWrapper = useCallback(
    (bamId, sampleName) => {
      handleDownloadPacBioBam(bamId, sampleName, handleDownloadBamFailed)
    },
    [handleDownloadPacBioBam, handleDownloadBamFailed],
  )

  // Handle filter chip click
  const handleFilterChipClick = useCallback(
    (type) => {
      setFilters((prevFilters) => {
        const currentTypes = prevFilters.type

        if (type === 'All') {
          // If All is clicked, select only All
          return {
            ...prevFilters,
            type: ['All'],
          }
        }

        const isSelected = currentTypes.includes(type)

        if (isSelected) {
          // Remove from filters
          const newTypes = currentTypes.filter((t) => t !== type)
          // If no types selected, select All
          return {
            ...prevFilters,
            type: newTypes.length === 0 ? ['All'] : newTypes,
          }
        } else {
          // Add to filters, but remove All if it was selected
          const typesWithoutAll = currentTypes.filter((t) => t !== 'All')
          const newTypes = [...typesWithoutAll, type]

          // If all specific types are selected, automatically switch to All
          const hasAllSpecificTypes = availableTypes.every((t) => newTypes.includes(t))

          if (hasAllSpecificTypes) {
            return {
              ...prevFilters,
              type: ['All'],
            }
          }

          return {
            ...prevFilters,
            type: newTypes,
          }
        }
      })
      setPage(0) // Reset to first page when filters change
    },
    [availableTypes],
  )

  // Handle search change
  const handleSearchChange = useCallback((event) => {
    const value = event.target.value
    setSearchTerm(value)
    setPage(0) // Reset to first page when search changes
  }, [])

  // Column configuration with memoization
  const columns = useMemo(
    () =>
      createFilesTableColumns({
        processedData,
        annotationStatus,
        handleAnnotationModelOpen,
        downloadingFastqId,
        downloadingReportId,
        downloadingVcfId,
        handleDownloadFastq: handleDownloadFastqWrapper,
        handleDownloadReport,
        handleDownloadRawVcf,
        handleOpenAIDialog,
        setFileNotes,
        setFinishedInfo,
        handleFileDelete,
        optimisticUpdates: {},
        downloadingBamId,
        handleDownloadOntBam: handleDownloadOntBamWrapper,
        handleDownloadPacBioBam: handleDownloadPacBioBamWrapper,
      }),
    [
      processedData,
      annotationStatus,
      handleAnnotationModelOpen,
      downloadingFastqId,
      downloadingReportId,
      downloadingVcfId,
      handleDownloadFastqWrapper,
      handleDownloadReport,
      handleDownloadRawVcf,
      handleOpenAIDialog,
      setFileNotes,
      setFinishedInfo,
      handleFileDelete,
      downloadingBamId,
      handleDownloadOntBamWrapper,
      handleDownloadPacBioBamWrapper,
    ],
  )

  // Show loading spinner only for initial load
  const isInitialLoading = isLoadingState && !isPreviousData

  return (
    <>
      {/* Dialogs */}
      <ReadOnlyAnnotationDialog open={aiDialogOpen} onClose={handleCloseAIDialog} ai={selectedAI} />

      <NewAnnotationDialog
        open={isAnnotationModalOpen}
        onClose={handleAnnotationModelClose}
        onClickAnnotate={handleFileAnnotationWrapper}
        fileType={
          selectedFile?.fastq_pair_id
            ? 'FASTQ'
            : selectedFile?.ont_bam_id
            ? 'ONT_BAM'
            : selectedFile?.pacbio_bam_id
            ? 'PACBIO_BAM'
            : 'VCF'
        }
        title={selectedFile?.sample_name}
        bedFiles={bedFiles}
        bedFilesApi={bedFilesApi}
        key={'create-analysis-dialog'}
        data={processedData}
        currentId={
          selectedFile?.fastq_pair_id || selectedFile?.ont_bam_id || selectedFile?.pacbio_bam_id || selectedFile?.vcf_id
        }
        vcfId={vcfIdState}
      />

      {/* Table */}
      <Box className={classes.wrapper}>
        {/* Title, Search, and Filter Chips on same line */}
        <Box className={classes.filtersContainer}>
          <Typography variant="h6">Files</Typography>
          <Box className={classes.searchContainer}>
            <TextField
              className={classes.searchField}
              placeholder="Search files on sample name or filename (min 3 chars)..."
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Box className={classes.filterChips}>
              {/* Select All chip */}
              <Chip
                key="All"
                label="All"
                color={filters.type.includes('All') ? 'primary' : 'default'}
                variant={filters.type.includes('All') ? 'default' : 'outlined'}
                onClick={() => handleFilterChipClick('All')}
                clickable
                size="small"
              />
              {availableTypes
                .filter((type) => type !== 'All')
                .map((type) => {
                  const isSelected = filters.type.includes(type)
                  return (
                    <Chip
                      key={type}
                      label={type}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'default' : 'outlined'}
                      onClick={() => handleFilterChipClick(type)}
                      clickable
                      size="small"
                    />
                  )
                })}
            </Box>
          </Box>
        </Box>

        <Box className={classes.tableContainer}>
          <MUIDataTable
            title=""
            data={processedData}
            columns={columns}
            options={{
              filterType: 'multiselect',
              responsive: 'standard',
              elevation: 0,
              selectableRows: 'none',
              serverSide: true,
              count: totalCount,
              page: page,
              rowsPerPage: rowsPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onChangePage: handleChangePage,
              onChangeRowsPerPage: handleChangeRowsPerPage,
              search: false,
              sort: true,
              filter: false,
              print: false,
              download: false,
              viewColumns: false,
              onColumnSortChange: handleSortChange,
              loading: isLoadingState,
              textLabels: {
                body: {
                  noMatch: isInitialLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                      <CircularProgress />
                    </div>
                  ) : (
                    'Sorry, no matching records found'
                  ),
                  toolTip: 'Sort',
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Error Dialog */}
      <Dialog open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
        <DialogTitle>Insufficient Balance</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            You do not have enough balance to perform the annotation, please increase your balance by {neededBalance}{' '}
            Credit(s).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <FileTableSnackbars
        refundSnackbarOpen={refundSnackbarOpen}
        onRefundSnackbarClose={handleRefundSnackbarClose}
        refundedFile={refundedFile}
        refundAmount={refundAmount}
        downloadFastqSnackbarOpen={downloadFastqSnackbarOpen}
        onDownloadFastqSnackbarClose={handleDownloadFastqSnackbarClose}
        downloadFastqFailedSnackbarOpen={downloadFastqFailedSnackbarOpen}
        onDownloadFastqFailedSnackbarClose={handleDownloadFastqFailedSnackbarClose}
        downloadBamFailedSnackbarOpen={downloadBamFailedSnackbarOpen}
        onDownloadBamFailedSnackbarClose={handleDownloadBamFailedSnackbarClose}
        paymentSnackbarOpen={paymentSnackbarOpen}
        onPaymentSnackbarClose={handlePaymentSnackbarClose}
        deductedAmount={deductedAmount}
        newBalance={newBalance}
      />
    </>
  )
})

FilesTable.displayName = 'FilesTable'

export default FilesTable
